import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FlaskConical, ShieldCheck, FileSpreadsheet, Clock, Loader2 } from "lucide-react";

const formatErr = (d) => {
  if (!d) return "Login failed";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(d);
};

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 bg-[#3b2418] relative overflow-hidden flex-col justify-between p-12">
        <div className="absolute inset-0 opacity-15"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1770195957512-b45ce419c00c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative flex items-center gap-3">
          <img src="/logo.png" alt="Vera" className="w-12 h-12 rounded-xl bg-[#faf6f0] p-1 object-contain" />
          <div className="font-heading text-2xl font-bold text-white">Vera</div>
        </div>
        <div className="relative">
          <h2 className="font-heading text-4xl font-bold text-white leading-tight">Pharmacy operations,<br /><span className="text-pink-300">digitized end-to-end.</span></h2>
          <p className="text-[#f7d8c8] mt-4 max-w-md">Invoices, SOP catalog, licensing, compliance reminders, records, and the time clock — one compliant control center.</p>
          <div className="flex gap-6 mt-8">
            {[[FileSpreadsheet, "AI Invoices"], [ShieldCheck, "Compliance"], [Clock, "Time Clock"]].map(([Icon, t]) => (
              <div key={t} className="flex items-center gap-2 text-[#f7d8c8] text-sm"><Icon className="w-4 h-4 text-pink-300" /> {t}</div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-[#f7d8c8]/60">USP &lt;797&gt; / &lt;800&gt; aligned workflows</div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-[#faf6f0]">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <img src="/logo.png" alt="Vera" className="w-10 h-10 object-contain" />
            <span className="font-heading text-xl font-bold text-[#3b2418]">Vera</span>
          </div>
          <h1 className="font-heading text-2xl font-bold text-[#3b2418]">Welcome back</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">Sign in to your pharmacy operations hub.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-sm text-slate-700">Email</Label>
              <Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@pharmacy.com" className="mt-1" required />
            </div>
            <div>
              <Label className="text-sm text-slate-700">Password</Label>
              <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1" required />
            </div>
            <Button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full bg-pink-700 hover:bg-pink-800 h-11">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
